import { Tab } from '@headlessui/react';
import { useState, useEffect } from 'react';
import { TabPaymentUpload } from './tabs/TabPaymentUpload';
import { TabPaymentEdit } from './tabs/TabPaymentEdit';
import { TabPaymentHistory } from '../../profile/tabs/TabPaymentHistory';
import { DocumentCurrencyDollarIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export const TabsPaymentStudent = ({ isOpen, userId, canUploadPayment, hasRejectedPayment, rejectedPayment, onRegisterFooterActions, onOpenProfileDrawer }) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    // Función para cambiar al tab de historial (expuesta para que TabPaymentUpload/TabPaymentEdit puedan llamarla)
    const switchToHistoryTab = () => {
        // Encontrar el índice del tab de historial en los tabs visibles
        const visibleTabs = tabsList.filter(tab => tab.allowView);
        const historyTabIndex = visibleTabs.findIndex(tab => tab.label === 'historial');
        if (historyTabIndex !== -1) {
            setSelectedTabIndex(historyTabIndex);
        }
    };

    // Lista de tabs
    const tabsList = [
        {
            label: 'editar pago',
            allowView: hasRejectedPayment, // Mostrar si hay un pago rechazado
            icon: <PencilSquareIcon className="w-5 h-5" />
        },
        {
            label: 'subir pago',
            allowView: canUploadPayment && !hasRejectedPayment, // Mostrar si puede subir pago y NO tiene pago rechazado
            icon: <DocumentCurrencyDollarIcon className="w-5 h-5" />
        },
        {
            label: 'historial',
            allowView: true,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        }
    ];

    // Filtrar tabs basado en allowView
    const visibleTabs = tabsList.filter(tab => tab.allowView);

    // Reset tab index cuando cambia el usuario o se abre el drawer
    useEffect(() => {
        setSelectedTabIndex(0);
    }, [userId, isOpen]);

    // Validar que el índice seleccionado sea válido
    useEffect(() => {
        if (selectedTabIndex >= visibleTabs.length) {
            setSelectedTabIndex(0);
        }
    }, [visibleTabs.length, selectedTabIndex]);

    return (
        <div className="w-full h-full flex flex-col">
            <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
                {() => (
                    <div className="h-full flex flex-col">
                        {/* Scroll horizontal en móvil, barra de scroll oculta */}
                        <div className="flex-shrink-0 border-b border-gray-200 bg-white overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <Tab.List className="flex border-b-0 min-w-max md:min-w-0 md:w-full">
                                {visibleTabs.map((tab) => {
                                    const { label, icon } = tab;
                                    return (
                                        <Tab
                                            key={label}
                                            className={({ selected }) =>
                                                classNames(
                                                    'group relative flex items-center justify-center gap-2 flex-shrink-0 md:flex-1 px-4 py-3.5 text-sm font-semibold transition-all duration-200 focus:outline-none',
                                                    selected
                                                        ? 'text-indigo-600'
                                                        : 'text-gray-600 hover:text-indigo-500 hover:bg-gray-50'
                                                )
                                            }
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span className={classNames(
                                                        'transition-all duration-200',
                                                        selected ? 'scale-110' : 'group-hover:scale-105'
                                                    )}>
                                                        {icon}
                                                    </span>
                                                    <span className="capitalize tracking-wide whitespace-nowrap">
                                                        {label}
                                                    </span>
                                                    {/* Indicador inferior animado */}
                                                    {selected && (
                                                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/50" />
                                                    )}
                                                </>
                                            )}
                                        </Tab>
                                    )
                                })}
                            </Tab.List>
                        </div>
                        <Tab.Panels className="bg-white flex-1 overflow-auto">
                            {visibleTabs.map((tab, index) => {
                                const { label } = tab;
                                return (
                                    <Tab.Panel
                                        key={index}
                                        className={classNames(
                                            'p-6 focus:outline-none h-full',
                                            'transition-all duration-300 ease-in-out'
                                        )}
                                    >
                                        {label === 'editar pago' && <TabPaymentEdit userId={userId} isOpen={isOpen} onRegisterFooterActions={onRegisterFooterActions} onUploadSuccess={switchToHistoryTab} rejectedPayment={rejectedPayment} />}
                                        {label === 'subir pago' && <TabPaymentUpload userId={userId} isOpen={isOpen} onRegisterFooterActions={onRegisterFooterActions} onUploadSuccess={switchToHistoryTab} onOpenProfileDrawer={onOpenProfileDrawer} />}
                                        {label === 'historial' && <TabPaymentHistory />}
                                    </Tab.Panel>
                                )
                            })}
                        </Tab.Panels>
                    </div>
                )}
            </Tab.Group>
        </div>
    )
}
